import { useCallback, useRef, useState } from "react";
import {
  erc20Abi,
  type Abi,
  type Address,
  type TransactionReceipt,
} from "viem";
import {
  useAccount,
  usePublicClient,
  useReadContract,
  useWriteContract,
} from "wagmi";

import oftAbi from "@abi/common/OFT.json";
import { oftBridgePolicy } from "@config/oftBridgeConfig";
import { buildSendParam, type OftRoute } from "@lib/oftBridge";
import { isErrorRejectedByUser } from "@lib/utils";
import type { TxnErrorType } from "@services/wagmi";

export type BridgeSendStep =
  | { kind: "idle" }
  | { kind: "review" }
  | { kind: "approving" }
  | { kind: "sending" }
  | { kind: "done"; receipt: TransactionReceipt }
  | { kind: "error"; stage: "approve" | "send"; error: TxnErrorType };

type SubmitArgs = {
  amountLD: bigint;
  nativeFee: bigint;
  /** The connected address at the moment the user reviewed/confirmed. */
  expectedAddress?: Address;
};

const toTxnError = (error: unknown): TxnErrorType => {
  const e = error as { shortMessage?: string; message?: string };
  return {
    hasUserDenied: isErrorRejectedByUser(error),
    promptError: e?.shortMessage ?? e?.message ?? "Transaction failed",
  };
};

/**
 * A receipt with `status: "reverted"` is a RESOLVED promise, not a thrown
 * error — viem only throws on timeout/network failure. Treating resolution as
 * success would report a reverted (failed) transaction to the user as if
 * their funds were on their way.
 */
const revertedError = (
  context: "approval" | "bridge transfer"
): TxnErrorType => ({
  hasUserDenied: false,
  promptError: `The ${context} was mined but reverted on-chain. No ${
    context === "approval" ? "allowance was granted" : "funds were transferred"
  }. Please try again.`,
});

const useOftBridgeSend = ({
  route,
  onSuccess,
}: {
  route: OftRoute | null;
  /** Called once the send receipt resolves, so the caller can refresh balances. */
  onSuccess?: () => void;
}) => {
  const { address } = useAccount();
  const publicClient = usePublicClient({ chainId: route?.sourceChainId });
  const { writeContractAsync } = useWriteContract();

  const [step, setStep] = useState<BridgeSendStep>({ kind: "idle" });
  const lastSubmit = useRef<SubmitArgs | null>(null);
  const inFlightRef = useRef(false);

  const { data: allowance, refetch: refetchAllowance } = useReadContract({
    address: route?.tokenAddress,
    abi: erc20Abi,
    functionName: "allowance",
    chainId: route?.sourceChainId,
    args: address && route ? [address, route.spender] : undefined,
    query: {
      enabled: Boolean(route?.needsApproval && address),
      refetchInterval: oftBridgePolicy.allowanceRefetchMs,
    },
  });

  const currentAllowance = (allowance as bigint | undefined) ?? 0n;

  const hasAllowanceFor = useCallback(
    (amountLD: bigint) => !route?.needsApproval || currentAllowance >= amountLD,
    [route?.needsApproval, currentAllowance]
  );

  const openReview = useCallback(() => setStep({ kind: "review" }), []);
  const close = useCallback(() => {
    lastSubmit.current = null;
    setStep({ kind: "idle" });
  }, []);

  const submit = useCallback(
    async ({ amountLD, nativeFee, expectedAddress }: SubmitArgs) => {
      // Primary re-entrancy guard: synchronous flag. Set immediately to prevent
      // concurrent invocations from the same event loop tick before any re-render.
      if (inFlightRef.current) {
        return;
      }
      inFlightRef.current = true;

      try {
        // Defense in depth: also check step.kind, though the closure may be stale
        // if two clicks land in the same tick. This catches flows already in progress
        // and also deliberately refuses to retry from a done state (call close() to reset).
        if (
          step.kind === "approving" ||
          step.kind === "sending" ||
          step.kind === "done"
        ) {
          return;
        }

        if (!route || !address || !publicClient) return;

        // needsApprovalSnapshot (in the caller) was computed off the allowance of
        // whichever account was connected at review time. If the account changed
        // since then, that snapshot no longer describes the connected wallet: a
        // needed approval can be silently skipped and the send reverts on-chain.
        // Refuse to proceed rather than silently recompute.
        if (expectedAddress && address !== expectedAddress) {
          lastSubmit.current = null;
          setStep({
            kind: "error",
            stage:
              route.needsApproval && currentAllowance < amountLD
                ? "approve"
                : "send",
            error: {
              hasUserDenied: false,
              promptError:
                "Your wallet account changed since you reviewed this transaction. Please close this and start again.",
            },
          });
          return;
        }

        // Replayable until a hash is obtained. Once broadcast, the submission is
        // irreversible and must never be replayed by retry() — cleared as soon as
        // a hash exists, not after the receipt resolves (see below).
        lastSubmit.current = { amountLD, nativeFee, expectedAddress };

        if (route.needsApproval && currentAllowance < amountLD) {
          setStep({ kind: "approving" });
          let approveHash: `0x${string}`;
          try {
            approveHash = await writeContractAsync({
              address: route.tokenAddress,
              abi: erc20Abi,
              functionName: "approve",
              // Exact amount, not unlimited — matches how wrap/unwrap approves.
              args: [route.spender, amountLD],
              chainId: route.sourceChainId,
            });
          } catch (error) {
            setStep({
              kind: "error",
              stage: "approve",
              error: toTxnError(error),
            });
            return;
          }
          // A hash exists: the approval was broadcast. `waitForTransactionReceipt`
          // below has its own timeout and THROWS on timeout even though the
          // transaction is already on-chain — if that happened with lastSubmit
          // still set, "Try again" would replay the whole submit() and broadcast
          // a second approval. Clear it now so a timeout can't be replayed.
          lastSubmit.current = null;
          try {
            const approveReceipt = await publicClient.waitForTransactionReceipt(
              {
                hash: approveHash,
              }
            );
            if (approveReceipt.status === "reverted") {
              setStep({
                kind: "error",
                stage: "approve",
                error: revertedError("approval"),
              });
              return;
            }
            await refetchAllowance();
            // The approval is now confirmed on-chain (not merely broadcast), so
            // re-establish a replayable snapshot for the send leg below: if the
            // send fails before obtaining its own hash (e.g. a rejected
            // signature), retry() can safely redo just the send — the allowance
            // is already settled, so it can never re-trigger a duplicate approve.
            lastSubmit.current = { amountLD, nativeFee, expectedAddress };
          } catch (error) {
            setStep({
              kind: "error",
              stage: "approve",
              error: toTxnError(error),
            });
            return;
          }
        }

        setStep({ kind: "sending" });
        let sendHash: `0x${string}`;
        try {
          sendHash = await writeContractAsync({
            address: route.sourceContract,
            abi: oftAbi as Abi,
            functionName: "send",
            args: [
              buildSendParam({
                dstEid: route.dstEid,
                recipient: address,
                amountLD,
              }),
              { nativeFee, lzTokenFee: 0n },
              address,
            ],
            value: nativeFee,
            chainId: route.sourceChainId,
          });
        } catch (error) {
          setStep({ kind: "error", stage: "send", error: toTxnError(error) });
          return;
        }
        // A hash exists: the transfer is broadcast and irreversible. Clear
        // lastSubmit now — if the receipt wait below times out and throws, "Try
        // again" must never be able to replay this and send the user's funds a
        // second time.
        lastSubmit.current = null;
        try {
          const receipt = await publicClient.waitForTransactionReceipt({
            hash: sendHash,
          });
          if (receipt.status === "reverted") {
            setStep({
              kind: "error",
              stage: "send",
              error: revertedError("bridge transfer"),
            });
            return;
          }
          setStep({ kind: "done", receipt });
          // Wrap onSuccess in its own try-catch: if the callback throws, the send
          // is already complete and irreversible. A consumer's balance-refresh
          // failure must never flip the done state to error or re-enable retry.
          try {
            onSuccess?.();
          } catch {
            // Silently ignore callback errors — transaction succeeded.
          }
        } catch (error) {
          setStep({ kind: "error", stage: "send", error: toTxnError(error) });
        }
      } finally {
        inFlightRef.current = false;
      }
    },
    [
      step,
      route,
      address,
      publicClient,
      currentAllowance,
      writeContractAsync,
      refetchAllowance,
      onSuccess,
    ]
  );

  // True only when a replayable (not-yet-broadcast) submission exists. False
  // once a hash has been obtained (irreversible — see submit()) or after the
  // modal was dismissed mid-flight, so the UI can hide "Try again" instead of
  // offering a button that does nothing.
  const canRetry = lastSubmit.current !== null;

  const retry = useCallback(
    (overrides?: Partial<Pick<SubmitArgs, "nativeFee">>) => {
      if (!lastSubmit.current) return;
      void submit({ ...lastSubmit.current, ...overrides });
    },
    [submit]
  );

  return {
    step,
    hasAllowanceFor,
    canRetry,
    openReview,
    close,
    retry,
    submit,
  };
};

export default useOftBridgeSend;
